import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { patientFileService } from '../services/patientFileService'
import './PatientFileUpload.css'

interface PatientFileUploadProps {
  patientId: string
  onSuccess?: () => void
  onCancel?: () => void
}

export function PatientFileUpload({ patientId, onSuccess, onCancel }: PatientFileUploadProps) {
  const queryClient = useQueryClient()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('other')
  const [dragActive, setDragActive] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)

  const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!selectedFile) throw new Error('No file selected')
      return patientFileService.upload(patientId, {
        file: selectedFile,
        displayName: displayName.trim() || undefined,
        description: description.trim() || undefined,
        category: category || undefined,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-files', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-files'] })
      onSuccess?.()
      // Reset form
      setSelectedFile(null)
      setDisplayName('')
      setDescription('')
      setCategory('other')
      setFileError(null)
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`File size exceeds the maximum limit of ${formatFileSize(MAX_FILE_SIZE)}`)
        setSelectedFile(null)
        return
      }
      setFileError(null)
      setSelectedFile(file)
      // Auto-fill display name with filename (without extension) if empty
      if (!displayName.trim()) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
        setDisplayName(nameWithoutExt)
      }
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`File size exceeds the maximum limit of ${formatFileSize(MAX_FILE_SIZE)}`)
        setSelectedFile(null)
        return
      }
      setFileError(null)
      setSelectedFile(file)
      // Auto-fill display name with filename (without extension) if empty
      if (!displayName.trim()) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
        setDisplayName(nameWithoutExt)
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFile) {
      alert('Please select a file to upload')
      return
    }

    if (fileError) {
      alert(fileError)
      return
    }

    try {
      await uploadMutation.mutateAsync()
    } catch (error: any) {
      alert(error?.message || 'Failed to upload file')
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div className="patient-file-upload">
      <form onSubmit={handleSubmit}>
        <div className="upload-section">
          <div
            className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-input"
              onChange={handleFileSelect}
              className="file-input"
              accept="*/*"
            />
            <label htmlFor="file-input" className="file-input-label">
              {selectedFile ? (
                <div className="file-selected">
                  <span className="file-icon">📄</span>
                  <div className="file-info">
                    <div className="file-name">{selectedFile.name}</div>
                    <div className="file-size">{formatFileSize(selectedFile.size)}</div>
                  </div>
                  <button
                    type="button"
                    className="remove-file-btn"
                    onClick={(e) => {
                      e.preventDefault()
                      setSelectedFile(null)
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="file-placeholder">
                  <span className="upload-icon">📤</span>
                  <p>Drag and drop a file here, or click to browse</p>
                  <p className="hint">Any file type is supported</p>
                </div>
              )}
            </label>
          </div>
        </div>

        {fileError && (
          <div className="file-error" style={{ color: '#dc3545', marginBottom: '1rem', padding: '0.75rem', background: '#f8d7da', borderRadius: '6px' }}>
            {fileError}
          </div>
        )}

        <div className="form-fields">
          <div className="form-group">
            <label htmlFor="displayName">Display Name (optional)</label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="form-control"
              placeholder="Enter a custom name for this file..."
            />
            <small className="form-hint">If not provided, the original filename will be used</small>
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="form-control"
            >
              <option value="medical-record">Medical Record</option>
              <option value="insurance">Insurance</option>
              <option value="id">ID Document</option>
              <option value="referral">Referral</option>
              <option value="correspondence">Correspondence</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (optional)</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-control"
              rows={3}
              placeholder="Add a description for this file..."
            />
          </div>
        </div>

        <div className="form-actions">
          {onCancel && (
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={!selectedFile || uploadMutation.isPending}
          >
            {uploadMutation.isPending ? 'Uploading...' : 'Upload File'}
          </button>
        </div>
      </form>
    </div>
  )
}






























